/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import {
  GetRfqs200ResponseBlocksInnerLegsInner,
  SideResponse,
} from "../../../../codegen-api";
import { getUsdcDecimals } from "../../../../config/chains";
import { toTokenUnits } from "../../../../utils/decimalString";
import { ToastEnum, ToastStatusEnum } from "../../../../utils/toast";
import { useSFX } from "../../../../hooks/useSFX";
import { useToast } from "../../../../hooks/toast";
import { ITakerQuotePayload } from "../../../../hooks/api/rfq/useQuotes";
import { IRFQModalProps } from "./RequestAcceptRFQModal.types";
import { buildLegTag } from "./RequestAcceptRFQModal.helpers";
import RequestAcceptRFQModalForm from "./RequestAcceptRFQModalForm";

/**
 * A quote figure as USDC base units. `parseUnits` threw on a quote carrying
 * more decimals than USDC (and on "1e-7"); the extra digits are floored instead.
 */
const toUsdcUnitsString = (value: string | number | undefined): string => {
  const units = toTokenUnits(value ?? "0", getUsdcDecimals());
  if (!units) {
    throw new Error("Invalid quote amount");
  }
  return units.toString();
};

function RequestAcceptRFQModal({
  block,
  side,
  show,
  onHide,
  acceptQuote,
  mutateRFQs,
}: IRFQModalProps) {
  const { playSound } = useSFX();
  const { addToast, addErrorToast } = useToast();
  const { t: apiErrors } = useTranslation("apiErrors");
  const [isLoading, setLoading] = useState<boolean>(false);
  const inFlightRef = useRef(false);

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    control,
    trigger,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      legs: (block?.legs || []).map((leg) => ({
        ratio: leg.ratio,
        instrument: leg.instrument_id,
        side: leg.side,
        price: undefined as number | undefined,
      })),
    },
  });

  useEffect(() => {
    if (!show) {
      reset();
    } else {
      setValue(
        "legs",
        (block?.legs || []).map((leg) => ({
          ratio: leg.ratio,
          instrument: leg.instrument_id,
          side: leg.side,
          price: undefined as number | undefined,
        }))
      );
    }
  }, [block?.legs, reset, setValue, show]);

  const legs = useWatch({ control, name: "legs" });

  const blockPrice = useMemo(() => {
    // If there are no legs, return 0
    if (!legs || legs.length === 0) {
      return 0;
    }

    // For each leg, depending on the side, calculate the price * ratio
    // If it's sell, deduct the price from the acc
    // If it's buy, add the price to the acc
    const amount = legs.reduce((acc, leg) => {
      if (leg.price && leg.price > 0) {
        if (leg.side === "sell") {
          return acc - leg.price * Number(leg.ratio);
        }
        return acc + leg.price * Number(leg.ratio);
      }

      return acc;
    }, 0);
    return amount;
  }, [legs]);

  const isTaker = useMemo(() => block?.role === "taker", [block?.role]);

  const bestOffer = useMemo(() => {
    if (isTaker && block?.orderbook && side) {
      return side === SideResponse.Buy
        ? block.orderbook?.bids?.[0][0]
        : block.orderbook?.asks?.[0][0];
    }
    return undefined;
  }, [isTaker, block, side]);

  const bestOfferQuantity = useMemo(() => {
    if (isTaker && block?.orderbook && side) {
      if (block.full_size) {
        return block.amount;
      }

      return side === SideResponse.Buy
        ? block.orderbook?.bids?.[0][1]
        : block.orderbook?.asks?.[0][1];
    }
    return undefined;
  }, [isTaker, block, side]);

  const invertedSide = useCallback((s: SideResponse) => {
    if (s === SideResponse.Buy) {
      return SideResponse.Sell;
    }
    return SideResponse.Buy;
  }, []);

  const tagSide = useCallback(
    (legSide: SideResponse) => {
      // Inverse the bid if it's a taker
      if (isTaker && side) {
        if (side === SideResponse.Buy) {
          return invertedSide(legSide);
        }

        return legSide;
      }

      // Inverse the ask if it's a maker
      return side === SideResponse.Sell ? invertedSide(legSide) : legSide;
    },
    [isTaker, invertedSide, side]
  );

  const generateLegTag = useCallback(
    (leg: GetRfqs200ResponseBlocksInnerLegsInner): Array<string | JSX.Element> =>
      buildLegTag(leg, tagSide),
    [tagSide]
  );

  const tags = useMemo(() => {
    if (block?.legs && block.legs.length > 0) {
      return block.legs.map((leg) => generateLegTag(leg));
    }

    return undefined;
  }, [block, generateLegTag]);

  const onSubmit = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      if (block?.block_id) {
        const payload: ITakerQuotePayload = {
          block_id: block.block_id || "",
          is_buy: isTaker
            ? side !== SideResponse.Buy
            : side === SideResponse.Buy,
          limit_price: isTaker ? toUsdcUnitsString(bestOffer || "0") : undefined,
          amount: toUsdcUnitsString(
            isTaker ? bestOfferQuantity || "0" : block.amount
          ),
          legs: isTaker
            ? []
            : legs.map((leg) => ({
                instrument: Number(leg.instrument),
                price: toUsdcUnitsString(leg.price),
              })),
        };

        const response = await acceptQuote(payload);

        if (response) {
          mutateRFQs();
          playSound("order_placed", true);
          reset();
          addToast(
            {
              type: ToastEnum.SIMPLE,
              header: "RFQ Created",
              subheader: "RFQ created successfully",
              status: ToastStatusEnum.SUCCESS,
            },
            4000
          );
        }
      }
    } catch (error: any) {
      addErrorToast(
        "Create RFQ failed",
        apiErrors(error.message) || t("try_again")
      );
    }

    setLoading(false);
    inFlightRef.current = false;
    onHide?.();
  }, [
    acceptQuote,
    addErrorToast,
    addToast,
    apiErrors,
    bestOffer,
    bestOfferQuantity,
    block?.amount,
    block?.block_id,
    isTaker,
    legs,
    mutateRFQs,
    onHide,
    playSound,
    reset,
    side,
  ]);

  const offerButtonLabel = useMemo(() => {
    if (isTaker) {
      if (side === SideResponse.Buy) {
        return SideResponse.Sell;
      }
      return SideResponse.Buy;
    }
    if (side === SideResponse.Sell) {
      return "Ask";
    }
    return "Bid";
  }, [isTaker, side]);

  if (!block || !side) {
    return null;
  }

  return (
    <RequestAcceptRFQModalForm
      block={block}
      side={side}
      show={show}
      onHide={onHide}
      isTaker={isTaker}
      tags={tags}
      register={register}
      setValue={setValue}
      trigger={trigger}
      handleSubmit={handleSubmit}
      errors={errors}
      legs={legs}
      onSubmit={onSubmit}
      bestOffer={bestOffer}
      blockPrice={blockPrice}
      isLoading={isLoading}
      isValid={isValid}
      invertedSide={invertedSide}
      offerButtonLabel={offerButtonLabel}
    />
  );
}

export default RequestAcceptRFQModal;
